import apiClient from './client';
import type { TeamName, RoundNumber, ScoreByType } from '@business-game/shared';

/**
 * Game API Service
 *
 * Handles communication with Phase 3 game endpoints:
 * - Save round decisions
 * - Calculate scores
 * - Generate verbatims
 */

// Types
export interface SaveRoundRequest {
  criteria_id: number;
  points: number;
  round: number;
  team_name: TeamName;
  session?: string;
}

export interface SaveRoundBatchRequest {
  decisions: Array<{ criteriaId: number; points: number }>;
  roundNumber: RoundNumber;
  teamName: TeamName;
  sessionCode?: string;
}

export interface CalculateScoreRequest {
  teamName: TeamName;
  currentRound: RoundNumber;
  sessionCode?: string;
}

export interface GenerateVerbatimRequest {
  type_1: number;
  type_2: number;
  type_3: number;
  type_4: number | null;
  lng: string;
  session?: string;
}

export interface VerbatimResponse {
  verbatim_1: string | null;
  verbatim_2: string | null;
  verbatim_3: string | null;
  verbatim_4: string | null;
  html: string;
}

export const gameApi = {
  /**
   * Save a single round decision
   * POST /api/game/rounds
   */
  async saveRound(data: SaveRoundRequest): Promise<string> {
    const response = await apiClient.post('/game/rounds', data);
    // PHP returns "OK" as plain text
    return response.data;
  },

  /**
   * Save multiple round decisions in a transaction
   * POST /api/game/rounds/batch
   */
  async saveRoundBatch(data: SaveRoundBatchRequest): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post('/game/rounds/batch', data);
    return response.data;
  },

  /**
   * Calculate scores for a team up to a specific round
   * POST /api/game/scores/calculate
   */
  async calculateScore(data: CalculateScoreRequest): Promise<ScoreByType> {
    const response = await apiClient.post('/game/scores/calculate', data);
    return response.data.data;
  },

  /**
   * Generate verbatims based on score thresholds (HTML response)
   * POST /api/game/verbatims/generate
   */
  async generateVerbatimsHtml(data: GenerateVerbatimRequest): Promise<string> {
    const response = await apiClient.post('/game/verbatims/generate', data);
    // Returns HTML string directly
    return response.data;
  },

  /**
   * Generate verbatims based on score thresholds (JSON response)
   * POST /api/game/verbatims/generate/json
   */
  async generateVerbatims(data: GenerateVerbatimRequest): Promise<VerbatimResponse> {
    const response = await apiClient.post('/game/verbatims/generate/json', data);
    return response.data.data;
  },

  /**
   * Get all decisions for a team
   * GET /api/game/rounds/:teamName
   */
  async getRoundDecisions(
    teamName: TeamName,
    maxRound?: RoundNumber,
    sessionCode?: string
  ): Promise<
    Array<{
      criteriaId: number;
      roundNumber: number;
      points: number;
      timestamp: Date;
    }>
  > {
    const params: Record<string, string | number> = {};
    if (maxRound) params.maxRound = maxRound;
    if (sessionCode) params.sessionCode = sessionCode;

    const response = await apiClient.get(`/game/rounds/${teamName}`, { params });
    return response.data.data;
  },

  /**
   * Reset all decisions for a team (admin)
   * DELETE /api/game/rounds/:teamName
   */
  async resetTeam(teamName: TeamName, sessionCode?: string): Promise<{ success: boolean; deletedCount: number }> {
    const params: Record<string, string> = {};
    if (sessionCode) params.sessionCode = sessionCode;

    const response = await apiClient.delete(`/game/rounds/${teamName}`, { params });
    return response.data;
  },

  /**
   * Reset the team's decisions from the player side (logout).
   * Public endpoint scoped by session code.
   * POST /api/game/rounds/:teamName/player-reset
   */
  async resetTeamAsPlayer(
    teamName: TeamName,
    sessionCode: string
  ): Promise<{ success: boolean; deletedCount: number }> {
    const response = await apiClient.post(`/game/rounds/${teamName}/player-reset`, { sessionCode });
    return response.data;
  },

  /**
   * Generate graph data for a team
   * POST /api/game/graphs/generate
   * Replaces: app/generate.user.graph.ajax.php
   */
  async generateGraph(data: { team: number; round: RoundNumber; session?: string }): Promise<{
    business: number[];
    employees: number[];
    society: number[];
    environment: number[];
    labels: string[];
  }> {
    const response = await apiClient.post('/game/graphs/generate', data);
    return response.data;
  },
};
